import { COUNTRIES_WITH_SINGLE_VERIFICATION_FLOW } from 'constants/constants';
import { getDateTime, getDiff } from 'helpers/day';
import { isUserIndian } from 'helpers/user';
import {
  isKycRefreshExpired,
  isKycRequiredDeadlineCrossed,
  isUserNonIndianAndReduceOnly,
} from 'helpers/utils';
import i18n from 'i18n/config';
import IUser from 'types/IUser';

type KYCVerificationType = {
  isVerified: boolean;
  label: string;
  verificationStatus: string;
};

interface KYCVerificationInterface {
  isReduceOnlyEnable: boolean;
  isUserIndian: boolean;
  isKycExpired: boolean;
  isKycRequired: boolean;
  isVerified: boolean;
  verificationStatus: string;
  isOldUser: boolean;
  isUserNonIndianAndOld: boolean;
  kycLabel: string;
  isPhoneVerified: boolean;
  isOnlyPhoneVerified: boolean;
  isKycRefreshRequired: boolean;
  isKycProvisioned: boolean;
  isUserOldAndUnverified: boolean;
  country: string;
}

/* Users registered before this date are considered as OLD Users  */
const KYC_UPDATE_DATE = '2023-01-06T12:30:00.000Z';

const KYC_LABELS = {
  BASIC: 'basic',
  VERIFIED: 'verified',
  VERIFIED_PLUS: 'verified_plus',
  UNVERIFIED: 'unverified',
};

const isOldUser = (user: IUser, isPhoneVerified: boolean): boolean => {
  const kycUpdateDate = new Date(KYC_UPDATE_DATE);
  const userRegistrationDate = getDateTime(user?.profile?.registration_date);
  const isUserRegisteredAfterKYCUpdateDate =
    getDiff(userRegistrationDate, getDateTime(kycUpdateDate), 'milliseconds') > 0;

  /* If user registered after KYC_UPDATE_DATE, then the user is new user */
  if (isUserRegisteredAfterKYCUpdateDate) return false;

  /*
    If user registered before KYC_UPDATE_DATE and
    1. phone number not verified till now, then its a new user
    2. phone number is verified after KYC_UPDATE_DATE, then its a new user
    3. phone number is verified before KYC_UPDATE_DATE, then its a new user
  */
  if (!isPhoneVerified) return false;
  const isPhoneVerifiedAfterKYCUpdateDate =
    getDiff(
      getDateTime(user?.phone_verified_on),
      getDateTime(kycUpdateDate),
      'milliseconds'
    ) > 0;

  return !isPhoneVerifiedAfterKYCUpdateDate;
};

const getKYCStatus = (
  user: IUser,
  isUserNonIndianAndOld: boolean,
  isUserNonIndianAndNew: boolean,
  isOnlyPhoneVerified: boolean
): KYCVerificationType => {
  const {
    country,
    is_kyc_done: isKycDone,
    proof_of_identity_status: proofOfIdentityStatus,
    proof_of_address_status: proofOfAddressStatus,
  } = user;

  const status: KYCVerificationType = {
    isVerified: false,
    label: KYC_LABELS.UNVERIFIED,
    verificationStatus: i18n.t('account:profile.unverified'),
  };

  if (COUNTRIES_WITH_SINGLE_VERIFICATION_FLOW.includes(country!)) {
    if (isKycDone) {
      status.isVerified = true;
      status.label = KYC_LABELS.VERIFIED;
      status.verificationStatus = i18n.t('account:profile.kycLevelNames.verified');
    }
  } else if (isUserNonIndianAndOld) {
    if (isOnlyPhoneVerified) {
      status.isVerified = true;
      status.label = KYC_LABELS.BASIC;
      status.verificationStatus = i18n.t('account:profile.kycLevelNames.basic');
    }

    if (proofOfIdentityStatus === 'approved' && proofOfAddressStatus !== 'approved') {
      status.isVerified = true;
      status.label = KYC_LABELS.VERIFIED;
      status.verificationStatus = i18n.t('account:profile.verified');
    }

    if (proofOfAddressStatus === 'approved') {
      status.isVerified = true;
      status.label = KYC_LABELS.VERIFIED_PLUS;
      status.verificationStatus = i18n.t('account:profile.kycLevelNames.verifiedPlus');
    }
  } else if (isUserNonIndianAndNew) {
    if (proofOfIdentityStatus === 'approved' && proofOfAddressStatus !== 'approved') {
      status.isVerified = true;
      status.label = KYC_LABELS.VERIFIED;
      status.verificationStatus = i18n.t('account:profile.kycLevelNames.verified');
    }

    if (proofOfAddressStatus === 'approved') {
      status.isVerified = true;
      status.label = KYC_LABELS.VERIFIED_PLUS;
      status.verificationStatus = i18n.t('account:profile.kycLevelNames.verifiedPlus');
    }
  } else {
    status.isVerified = false;
    status.label = KYC_LABELS.UNVERIFIED;
    status.verificationStatus = i18n.t('account:profile.unverified');
  }

  return status;
};

/**
 * Computes all KYC-related status information for a user
 * @param user - The user object
 * @returns KYC verification interface with all computed properties
 */
const getKYCStatusDetails = (user: IUser): KYCVerificationInterface => {
  const isKycExpired = isKycRefreshExpired(user);
  const isKycRequired = isKycRequiredDeadlineCrossed(user);
  const isUserReduceOnlyMode = isUserNonIndianAndReduceOnly(user);
  const isIndian = isUserIndian(user);

  const {
    country,
    is_kyc_provisioned: isKycProvisioned,
    is_kyc_refresh_required: isKycRefreshRequired,
    phone_verification_status: phoneVerificationStatus,
    proof_of_identity_status: proofOfIdentityStatus,
    proof_of_address_status: proofOfAddressStatus,
  } = user;

  const isReduceOnlyEnable = isUserReduceOnlyMode || isKycExpired || isKycRequired;
  const isPhoneVerified = phoneVerificationStatus === 'verified';

  const isOld = isOldUser(user, isPhoneVerified);
  const isUserNonIndianAndOld = !isIndian && isOld;
  const isUserNonIndianAndNew = !isIndian && !isOld;

  const isOnlyPhoneVerified =
    isPhoneVerified &&
    proofOfIdentityStatus !== 'approved' &&
    proofOfAddressStatus !== 'approved';

  const {
    isVerified,
    verificationStatus,
    label: kycLabel,
  } = getKYCStatus(
    user,
    isUserNonIndianAndOld,
    isUserNonIndianAndNew,
    isOnlyPhoneVerified
  );

  return {
    isReduceOnlyEnable,
    isUserIndian: isIndian,
    isKycExpired,
    isKycRequired,
    isVerified,
    verificationStatus,
    isOldUser: isOld,
    isUserOldAndUnverified: isOld && !isVerified,
    isUserNonIndianAndOld,
    kycLabel,
    isPhoneVerified,
    isOnlyPhoneVerified,
    isKycRefreshRequired: isKycRefreshRequired!,
    isKycProvisioned: isKycProvisioned!,
    country: country!,
  };
};

export type { KYCVerificationInterface };
export { KYC_LABELS, KYC_UPDATE_DATE, getKYCStatusDetails, isOldUser };
